import { NumberInput } from '@lightspeed/design-system-react'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useT } from '@transifex/react'
import { NoteInput } from '../shared/NoteInput'
import { useAutoGrowTextArea } from './useAutoGrowTextArea'

type StoreCreditData = {
  amount: string
  notes: string
}
export type IssueStoreCreditRef = {
  getFormData: () => StoreCreditData
}
type Props = {
  formValidChanged: (valid: boolean) => void
}

const MIN = 0.01
export const IssueStoreCreditForm = forwardRef<IssueStoreCreditRef, Props>(
  (props, ref) => {
    const { formValidChanged } = props

    const [amount, setAmount] = useState('')
    const [isAmountDirty, setIsAmountDirty] = useState(false)
    const [amountError, setAmountError] = useState(true)
    const [notes, setNotes] = useState('')
    const [notesHasError, setNotesHasError] = useState(true)
    const [isNotesDirty, setIsNotesDirty] = useState(false)
    const notesRef = useRef<HTMLTextAreaElement>(null)

    const onAmountError = (event: React.ChangeEvent<HTMLInputElement>) => {
      setAmountError(true)
    }

    useAutoGrowTextArea(notesRef, 3, 5)

    useImperativeHandle(
      ref,
      () => {
        return {
          getFormData() {
            return {
              amount,
              notes,
            }
          },
        }
      },
      [amount, notes]
    )

    /**
     * reset amount error on changed so form validity check is correct
     * set min of 0.01 once amount changed
     */
    const onAmountChanged = (e: number) => {
      setAmountError(false)
      setIsAmountDirty(true)
      setAmount(e.toString())
    }

    const onNotesChanged = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNotes(e.target.value)
      setIsNotesDirty(true)
    }

    useEffect(() => {
      let valid = true
      if (notesHasError) {
        valid = false
      } else if (amountError || Number(amount) < MIN) {
        valid = false
      }
      formValidChanged(valid)
    }, [formValidChanged, amountError, amount, notesHasError])

    const onNoteValidChange = useCallback(
      (valid: boolean) => {
        setNotesHasError(!valid || (notes === '' && isNotesDirty))
      },
      [notes, isNotesDirty]
    )

    const t = useT()
    return (
      <form id="issueStoreCreditCtrlForm" autoComplete="off">
        <div className="vd-field">
          <div className="vd-label">
            {t('Store credit value', {
              _context: 'Label for store credit value field',
            })}
          </div>
          <div className="vd-value">
            <NumberInput
              value={amount}
              onChange={onAmountChanged}
              placeholder={'0.00'}
              align="left"
              validators={{
                nonNegative: true,
                nonEmpty: !!isAmountDirty,
                min: !!isAmountDirty && MIN,
              }}
              onError={onAmountError}
              autoFocus={true}
              data-testid="store-credit-amount"
            />
          </div>
        </div>
        <div className="vd-field">
          <div className="vd-label">
            {t('Notes', { _context: 'Label for notes field' })}{' '}
            <span className="vd-text--sub vd-text--secondary">
              {t('Required', {
                _context: 'Text indicating a field is required',
              })}
            </span>
          </div>
          <NoteInput
            note={notes}
            charLimit={255}
            onChange={onNotesChanged}
            onValidChange={onNoteValidChange}
            dataTestId="store-credit-notes"
            hasError={isNotesDirty && notesHasError}
          />
        </div>
      </form>
    )
  }
)
